import React, { useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import useCompletionAnimation from '@r1-frontend/shared/hooks/useCompletionAnimation';

import MovingBar, { TMovingBarProps } from './MovingBar';

const AnimateMovingBar = (
    {
        viewBoxWidth = 500,
        dotsCount,
        startDot = 0,
        onDotSelected = () => null,
        ...props
    }: Omit<TMovingBarProps, 'dotsCount'> & { dotsCount: number, startDot?: number, onDotSelected?: (dot: number) => void },
): JSX.Element => {
    if (dotsCount < 2) {
        throw new Error('The MovingBar component Error. The "dotsCount" argument must be greater than 1');
    }

    if (startDot > dotsCount) {
        throw new Error('The MovingBar component Error. The "selectedDot" argument value can not be greater than the "dotsCount" argument');
    }

    const wrapperRef = useRef<HTMLDivElement>(null);

    const [distance, setDistance] = useState({
        start: 0,
        current: 0,
        needToWalk: 0,
        stepWidth: 1 / (dotsCount - 1),
    });

    const animation = useCompletionAnimation({
        duration: 150,
        timingFunction: timeFraction => 1 - Math.sin(Math.acos(timeFraction)),
        callback: (delta) => {
            setDistance(prev => ({ ...prev, current: prev.start + (prev.needToWalk - prev.start) * delta }));
        },
        onEndCallback: () => {
            setDistance(prev => ({ ...prev, start: prev.current }));
            onDotSelected(Math.round((dotsCount - 1) * distance.current));
        },
    });
    const swipeHandlers = useSwipeable({
        onSwiped: () => {
            setDistance(prev => ({ ...prev, start: prev.current }));
            // this lifehack needed for delay call of animation before processing the setDistance
            setTimeout(animation.start);
        },
        onSwiping: ({ dir, deltaX }) => {
            if (dir !== 'Left' && dir !== 'Right') {
                return;
            }

            let dist = distance.start + (deltaX / viewBoxWidth);

            if (dist > 1) {
                dist = 1;
            }

            if (dist < 0) {
                dist = 0;
            }

            setDistance(prev => ({ ...prev, current: dist, needToWalk: Math.round(prev.current / prev.stepWidth) * prev.stepWidth }));
        },
        // delta: 10,                             // min distance(px) before a swipe starts. *See Notes*
        preventScrollOnSwipe: true,           // prevents scroll during swipe (*See Details*)
        trackTouch: true,                      // track touch input
        trackMouse: true,                     // track mouse input
        rotationAngle: 0,                      // set a rotation angle
        swipeDuration: Infinity,               // allowable duration of a swipe (ms). *See Notes*
        touchEventOptions: { passive: true },  // options for touch listeners (*See Details*)
    });

    useEffect(() => {
        if (startDot >= 0) {
            setDistance(prev => ({
                ...prev,
                start: startDot / (dotsCount - 1),
                current: startDot / (dotsCount - 1),
                stepWidth: 1 / (dotsCount - 1),
            }));
        }
    }, [wrapperRef, viewBoxWidth, startDot, dotsCount]);

    const handleDotClick = (dotNumber: number) => () => {
        setDistance(prev => ({
            ...prev,
            needToWalk: dotNumber / (dotsCount - 1),
        }));
        setTimeout(animation.start);
    };

    return (
        <div ref={wrapperRef}>
            <MovingBar
                {...props}
                onDotClick={handleDotClick}
                dotsCount={dotsCount}
                useControlElement={true}
                viewBoxWidth={viewBoxWidth}
                onSwipeHandlers={swipeHandlers}
                distance={distance.current}
            />
        </div>
    );
};

export default AnimateMovingBar;
